import { Dispatch } from '@reduxjs/toolkit';
import { getStore } from 'redux/reemaStore.util';
import { ApiRequestError } from './apiRequest';

type ApiRequestErrorWithToastFlag = ApiRequestError & {
  errorToastShown?: boolean;
};

/**
 * Dispatches the standard API error toast.
 * @param error - Failed HTTP request error
 * @param dispatch - Redux dispatch
 */
export const setErrorToast = (error: ApiRequestError, dispatch: Dispatch) => {
  if (error.name !== 'HttpError' || error.body['statusCode'] === 400) {
    return;
  }

  const statusCode = error.body['statusCode'];

  // Suppress toasts for member access errors (handled by ProtectedMemberPage component)
  if (
    error.isAccessDenied === true &&
    (statusCode === 404 || statusCode === 401)
  ) {
    return;
  }

  // Suppress toasts for unauthenticated 401 errors (handled by ProtectedRoute)
  if (statusCode === 401) {
    const state = getStore().getState();
    if (!state.actingUser?.isAuthenticated) {
      return;
    }
  }

  const details = {
    path: error.body['path'],
    code: error.body['statusCode'],
    userMessage: error.body['userMessage'],
    timestamp: error.body['timestamp'],
    errors: error.body['errors'],
    link: error.body['link'],
  };

  dispatch({
    type: 'metaData/setToast',
    payload: {
      message: `An error has occurred: ${error?.body['userMessage']}.\n\nClick the copy button and paste error message in
#reema-app-help channel with steps taken when error occurred.`,
      severity: 'error',
      details,
    },
  });
};

/**
 * Shows {@link setErrorToast} at most once per error object instance.
 * Concurrent createApiThunk / useApiCall callers sharing one rejected in-flight
 * GET promise each enter catch; only the first should toast.
 * @param error - Failed HTTP request error (mutated with a one-shot flag)
 * @param dispatch - Redux dispatch
 */
export function setErrorToastOnce(
  error: ApiRequestError,
  dispatch: Dispatch
): void {
  const flagged = error as ApiRequestErrorWithToastFlag;
  if (flagged.errorToastShown) {
    return;
  }
  flagged.errorToastShown = true;
  setErrorToast(error, dispatch);
}
